import React, { useState, useMemo } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { PieChart as PieChartIcon, BarChart3, Download, Filter, TrendingUp } from 'lucide-react';
import { generateCategoryBreakdownData, formatPercentage, exportChart } from '../utils/visualization';
import { marketAnalysis } from '../data/market-analysis';
import type { FilterOptions, ExportConfig } from '../types/visualization';

interface CategoryBreakdownChartProps {
  filters?: FilterOptions;
  onFilterChange?: (filters: FilterOptions) => void;
  className?: string;
}

export const CategoryBreakdownChart: React.FC<CategoryBreakdownChartProps> = ({
  filters = {},
  onFilterChange,
  className = '',
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [chartType, setChartType] = useState<'pie' | 'bar'>('pie');
  const [dataView, setDataView] = useState<'share' | 'growth'>('share');

  const marketShareData = useMemo(() => generateCategoryBreakdownData(filters), [filters]);
  
  const growthData = useMemo(() => {
    return marketAnalysis.segment_analysis.map(segment => ({
      category: segment.segment.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value: segment.growth_rate,
      companyId: 'market',
      companyName: 'Growth Rate',
      color: `hsl(${Math.abs(segment.segment.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % 360}, 70%, 50%)`,
    }));
  }, []);

  const displayData = dataView === 'growth' ? growthData : marketShareData;

  const handleExport = async (format: 'png' | 'csv') => {
    const chartElement = document.getElementById('category-breakdown-chart');
    if (!chartElement) return;

    const config: ExportConfig = {
      format,
      filename: 'category-breakdown-chart',
      includeData: true,
    };

    await exportChart(config, chartElement, displayData);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <h3 className="font-semibold text-gray-900 mb-2">{label || data.payload.category}</h3>
          <div className="space-y-1 text-sm">
            <div className="flex items-center justify-between gap-4">
              <span className="text-gray-600">
                {dataView === 'growth' ? 'Growth Rate:' : 'Market Share:'}
              </span>
              <span className="font-medium">
                {dataView === 'growth' 
                  ? `${data.value.toFixed(1)}%`
                  : formatPercentage(data.value)
                }
              </span>
            </div>
            {dataView === 'share' && (
              <div className="pt-2 text-xs text-gray-500">
                Market size: ~${(400 * (data.value / 100)).toFixed(0)}B
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    if (percent < 0.05) return null; // Hide labels for very small segments
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="12"
        fontWeight="600"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Calculate insights
  const totalMarketSize = 400; // $400B from market analysis
  const largestSegment = displayData.reduce((prev, current) => 
    prev.value > current.value ? prev : current
  );
  const smallestSegment = displayData.reduce((prev, current) => 
    prev.value < current.value ? prev : current
  );

  return (
    <div className={`bg-white rounded-lg shadow-lg p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Service Category Analysis</h2>
          <p className="text-sm text-gray-600 mt-1">
            {dataView === 'growth' 
              ? 'Growth rates by service category' 
              : 'Market share distribution across service categories'
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-100 rounded-md p-1">
            <button
              onClick={() => setDataView('share')}
              className={`px-3 py-1 text-sm rounded ${
                dataView === 'share'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Market Share
            </button>
            <button
              onClick={() => setDataView('growth')}
              className={`px-3 py-1 text-sm rounded ${
                dataView === 'growth'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Growth Rate
            </button>
          </div>
          <div className="flex items-center bg-gray-100 rounded-md p-1">
            <button
              onClick={() => setChartType('pie')}
              className={`p-2 rounded ${
                chartType === 'pie'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <PieChartIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setChartType('bar')}
              className={`p-2 rounded ${
                chartType === 'bar'
                  ? 'bg-white shadow-sm text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <BarChart3 className="h-4 w-4" />
            </button>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleExport('png')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title="Export as PNG"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleExport('csv')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title="Export as CSV"
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Key Insights Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">
              {dataView === 'growth' ? 'Fastest Growing' : 'Largest Segment'}
            </span>
          </div>
          <div className="text-lg font-bold text-blue-900">{largestSegment.category}</div>
          <div className="text-sm text-blue-700">
            {dataView === 'growth' 
              ? `${largestSegment.value.toFixed(1)}% CAGR`
              : `${largestSegment.value.toFixed(1)}% share`
            }
          </div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-sm font-medium text-green-900 mb-2">Total Market Size</div>
          <div className="text-lg font-bold text-green-900">${totalMarketSize}B</div>
          <div className="text-sm text-green-700">2024 estimate</div>
        </div>
        <div className="bg-amber-50 rounded-lg p-4">
          <div className="text-sm font-medium text-amber-900 mb-2">
            {dataView === 'growth' ? 'Slowest Growing' : 'Smallest Segment'}
          </div>
          <div className="text-lg font-bold text-amber-900">{smallestSegment.category}</div>
          <div className="text-sm text-amber-700">
            {dataView === 'growth' 
              ? `${smallestSegment.value.toFixed(1)}% CAGR`
              : `${smallestSegment.value.toFixed(1)}% share`
            }
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Segment Filter</label>
              <select
                value={filters.segment || ''}
                onChange={(e) => onFilterChange?.({ ...filters, segment: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Segments</option>
                <option value="home_improvement">Home Improvement</option>
                <option value="cleaning_services">Cleaning Services</option>
                <option value="handyman_services">Handyman Services</option>
                <option value="moving_services">Moving Services</option>
                <option value="professional_services">Professional Services</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Chart Type</label>
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value as 'pie' | 'bar')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="pie">Pie Chart</option>
                <option value="bar">Bar Chart</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Chart */}
      <div id="category-breakdown-chart" className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'pie' ? (
            <PieChart>
              <Pie
                data={displayData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={CustomPieLabel}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {displayData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="bottom" 
                height={36}
                formatter={(value, entry) => (
                  <span style={{ color: entry.color }}>{value}</span>
                )}
              />
            </PieChart>
          ) : (
            <BarChart
              data={displayData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="category"
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
              />
              <YAxis 
                label={{ 
                  value: dataView === 'growth' ? 'Growth Rate (%)' : 'Market Share (%)', 
                  angle: -90, 
                  position: 'insideLeft' 
                }}
                tickFormatter={(value) => 
                  dataView === 'growth' ? `${value}%` : `${value}%`
                }
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {displayData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* Detailed Analysis */}
      <div className="mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Segment Details</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-900">Category</th>
                <th className="px-4 py-3 text-right font-medium text-gray-900">Market Share</th>
                <th className="px-4 py-3 text-right font-medium text-gray-900">Growth Rate</th>
                <th className="px-4 py-3 text-right font-medium text-gray-900">Market Size</th>
                <th className="px-4 py-3 text-left font-medium text-gray-900">Leading Players</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {marketAnalysis.segment_analysis.map((segment, index) => {
                const marketSize = (totalMarketSize * segment.market_share) / 100;
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {segment.segment.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {formatPercentage(segment.market_share)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {formatPercentage(segment.growth_rate)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      ${marketSize.toFixed(1)}B
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {segment.leading_companies.map(company => 
                        company.charAt(0).toUpperCase() + company.slice(1)
                      ).join(', ')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};